import React, { useState, useEffect } from 'react';
import { printerService } from '../../services/printer';
import { orgApi } from '../../api';
import { showToast } from '../../utils/toast';
import { encodePrintableInvoice } from '../../services/printer/escposEncoder';
import { buildPrintableInvoiceFromDraft } from '../../services/printer/printableInvoice';

// Sample invoice data for preview
const sampleInvoiceData = {
  billName: 'INV-20241201-001',
  customerName: 'John Doe',
  customerPhone: '+91-9876543210',
  items: [
    { name: 'Product A', price: 100, quantity: 2, unit: 'piece', discountPercent: 0 },
    { name: 'Product B', price: 250, quantity: 1, unit: 'kg', discountPercent: 10 },
    { name: 'Product C', price: 50, quantity: 3, unit: 'piece', discountPercent: 0 }
  ],
  total: 600,
  discount: 25,
  subtotal: 625
};

const PrinterPageEditor = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewText, setPreviewText] = useState('');
  
  // Printer settings state
  const [settings, setSettings] = useState({
    lineWidth: 32,
    feedLines: 4,
    autoCut: true,
    cutMode: 'partial',
    footerText: '',
    businessName: '',
    businessAddress: '',
    businessPhone: ''
  });

  // Load settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Generate preview when settings change
  useEffect(() => {
    if (!loading) {
      generatePreview();
    }
  }, [settings, loading]);

  const loadSettings = async () => {
    try {
      const orgData = await orgApi.get();
      if (orgData) {
        setSettings(prev => ({
          ...prev,
          footerText: orgData.footerText || '',
          businessName: orgData.name || '',
          businessAddress: orgData.address || '',
          businessPhone: orgData.phone || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      // Build printable invoice from sample data
      const printableInvoice = buildPrintableInvoiceFromDraft(
        sampleInvoiceData.billName,
        sampleInvoiceData.items,
        {
          name: sampleInvoiceData.customerName,
          phone: sampleInvoiceData.customerPhone
        },
        {
          name: settings.businessName || 'Your Business',
          address: settings.businessAddress,
          phone: settings.businessPhone,
          footerText: settings.footerText
        },
        {
          grandTotal: sampleInvoiceData.total,
          discount: sampleInvoiceData.discount,
          subTotal: sampleInvoiceData.subtotal
        },
        {
          footerText: settings.footerText
        }
      );

      // Encode to get preview
      const encoded = encodePrintableInvoice(printableInvoice, {
        lineWidth: settings.lineWidth,
        feedLines: settings.feedLines,
        autoCut: settings.autoCut,
        cutMode: settings.cutMode,
        footerText: settings.footerText || undefined
      });

      // Convert bytes to readable preview (simplified representation)
      const preview = generateTextPreview(printableInvoice, settings);
      setPreviewText(preview);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      showToast({ message: 'Failed to generate preview', variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateTextPreview = (printableInvoice, settings) => {
    const lineWidth = settings.lineWidth;
    const separator = '─'.repeat(lineWidth);
    
    let preview = '';
    
    // Business header
    preview += '\n';
    preview += ' '.repeat(Math.floor((lineWidth - (printableInvoice.business?.name || 'Business').length) / 2));
    preview += (printableInvoice.business?.name || 'Business').toUpperCase() + '\n';
    if (printableInvoice.business?.address) {
      preview += printableInvoice.business.address + '\n';
    }
    if (printableInvoice.business?.phone) {
      preview += `Phone: ${printableInvoice.business.phone}\n`;
    }
    preview += separator + '\n';
    
    // Invoice info
    preview += `Invoice: ${printableInvoice.invoiceNumber || '-'}\n`;
    preview += `Date: ${new Date().toLocaleString()}\n`;
    preview += separator + '\n';
    
    // Customer
    preview += `Customer: ${printableInvoice.customer?.name || 'Walk-in'}\n`;
    if (printableInvoice.customer?.phone) {
      preview += `Phone: ${printableInvoice.customer.phone}\n`;
    }
    preview += separator + '\n';
    
    // Items
    preview += 'Items\n';
    preview += separator + '\n';
    (printableInvoice.items || []).forEach(item => {
      const name = item.name || 'Item';
      const qty = item.quantity || 0;
      const price = item.unitPrice || 0;
      const total = item.lineTotal || (qty * price);
      const discount = item.discount || 0;
      
      preview += name + '\n';
      preview += `${qty} x Rs${price.toFixed(2)}\n`;
      if (discount > 0) {
        preview += `Discount: -Rs${discount.toFixed(2)}\n`;
      }
      preview += `Total: Rs${total.toFixed(2)}\n`;
      preview += '\n';
    });
    
    preview += separator + '\n';
    
    // Totals
    const totals = printableInvoice.totals || {};
    preview += `Subtotal: Rs${(totals.subTotal || 0).toFixed(2)}\n`;
    if (totals.discount > 0) {
      preview += `Discount: -Rs${totals.discount.toFixed(2)}\n`;
    }
    preview += `Total: Rs${(totals.grandTotal || 0).toFixed(2)}\n`;
    preview += separator + '\n';
    
    // Footer
    const footerText = settings.footerText || 'Thank you for your business!';
    preview += '\n';
    preview += ' '.repeat(Math.floor((lineWidth - footerText.length) / 2));
    preview += footerText + '\n';
    preview += '\n'.repeat(settings.feedLines || 4);
    
    if (settings.autoCut) {
      preview += '[AUTO CUT]\n';
    }
    
    return preview;
  };

  const saveSettings = async () => {
    try {
      await orgApi.update({
        footerText: settings.footerText
      });
      showToast({ message: 'Printer settings saved successfully!', variant: 'success' });
      if (onClose) onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
      showToast({ message: 'Failed to save settings', variant: 'error' });
    }
  };

  const testPrint = async () => {
    try {
      const printableInvoice = buildPrintableInvoiceFromDraft(
        sampleInvoiceData.billName,
        sampleInvoiceData.items,
        {
          name: sampleInvoiceData.customerName,
          phone: sampleInvoiceData.customerPhone
        },
        {
          name: settings.businessName || 'Your Business',
          address: settings.businessAddress,
          phone: settings.businessPhone,
          footerText: settings.footerText
        },
        {
          grandTotal: sampleInvoiceData.total,
          discount: sampleInvoiceData.discount,
          subTotal: sampleInvoiceData.subtotal
        },
        {
          footerText: settings.footerText
        }
      );

      await printerService.printInvoice(printableInvoice, {
        encoder: {
          lineWidth: settings.lineWidth,
          feedLines: settings.feedLines,
          autoCut: settings.autoCut,
          cutMode: settings.cutMode,
          footerText: settings.footerText || undefined
        }
      });
      
      showToast({ message: 'Test print sent to printer!', variant: 'success' });
    } catch (error) {
      console.error('Failed to test print:', error);
      showToast({ message: 'Failed to send test print', variant: 'error' });
    }
  };

  if (!open) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>
        
        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white border-b border-green-100 px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">🖨️ Printer Page Editor</h2>
                <p className="text-sm text-gray-600 mt-1">Customize your thermal printer output and preview</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Settings Panel */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-green-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Printer Settings</h3>
                  
                  {/* Line Width */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Line Width (characters)
                    </label>
                    <input
                      type="number"
                      value={settings.lineWidth}
                      onChange={(e) => setSettings(prev => ({ ...prev, lineWidth: parseInt(e.target.value) || 32 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      min="20"
                      max="48"
                    />
                  </div>

                  {/* Feed Lines */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Feed Lines (after footer)
                    </label>
                    <input
                      type="number"
                      value={settings.feedLines}
                      onChange={(e) => setSettings(prev => ({ ...prev, feedLines: parseInt(e.target.value) || 4 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      min="0"
                      max="10"
                    />
                  </div>

                  {/* Auto Cut */}
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoCut}
                        onChange={(e) => setSettings(prev => ({ ...prev, autoCut: e.target.checked }))}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Auto Cut Paper</span>
                    </label>
                  </div>

                  {/* Cut Mode */}
                  {settings.autoCut && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cut Mode
                      </label>
                      <select
                        value={settings.cutMode}
                        onChange={(e) => setSettings(prev => ({ ...prev, cutMode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="partial">Partial Cut</option>
                        <option value="full">Full Cut</option>
                      </select>
                    </div>
                  )}

                  {/* Footer Text */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Footer Text
                    </label>
                    <textarea
                      value={settings.footerText}
                      onChange={(e) => setSettings(prev => ({ ...prev, footerText: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows="2"
                      placeholder="Thank you for your business!"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use default: "Thank you for your business!"
                    </p>
                  </div>
                </div>

                {/* Business Info (for preview) */}
                <div className="bg-white rounded-lg border border-green-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Info (Preview Only)</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={settings.businessName}
                      onChange={(e) => setSettings(prev => ({ ...prev, businessName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Your Business"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <textarea
                      value={settings.businessAddress}
                      onChange={(e) => setSettings(prev => ({ ...prev, businessAddress: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows="2"
                      placeholder="Business address"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="text"
                      value={settings.businessPhone}
                      onChange={(e) => setSettings(prev => ({ ...prev, businessPhone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>

              {/* Preview Panel */}
              <div className="bg-white rounded-lg border border-green-100 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Preview</h3>
                  <button
                    onClick={testPrint}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    Test Print
                  </button>
                </div>
                
                {previewLoading ? (
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                    <pre className="text-xs font-mono whitespace-pre-wrap text-gray-800 bg-white p-4 rounded border border-gray-200 max-h-96 overflow-y-auto">
                      {previewText || 'Generating preview...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveSettings}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterPageEditor;

